import { gameLabel } from "@loot-os/fulfillment-core/catalog/game";
import type { BuyingQueueRow } from "@loot-os/fulfillment-core/procurement/queue";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { centsToDollars } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

const TABS = [
  { id: "queue", label: "Buying queue" },
  { id: "orders", label: "Buying orders" },
  { id: "receive", label: "Receive / dispositions" },
  { id: "uncosted", label: "Uncosted" },
  { id: "create", label: "Catalog create" },
  { id: "irregularities", label: "Irregularities" },
] as const;

type TabId = (typeof TABS)[number]["id"];

interface CartLine {
  catalogItemId: string;
  name: string;
  quantity: number;
  marketCents: number;
  decision: string;
}

const queueColumns = createDataTableColumns<BuyingQueueRow>();
const QUEUE_PINNED = { start: [], end: ["add"] };
const dollars = (value: unknown) => centsToDollars(Number(value));

/** " · urgent: Pokemon 12, MTG 3" over the games with any urgent quantity; "" when none. */
function urgentByGameSummary(byGame: Record<string, number>): string {
  const parts = Object.entries(byGame)
    .filter(([, quantity]) => quantity > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([game, quantity]) => `${gameLabel(game)} ${quantity}`);
  return parts.length ? ` · urgent: ${parts.join(", ")}` : "";
}

/** The queue's columns; `addToCart` is read through a ref so the column identity never changes. */
function buildQueueColumns(addToCart: { current: (row: BuyingQueueRow) => void }) {
  return queueColumns.columns([
    queueColumns.accessor("name", {
      header: "Item",
      size: 340,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.image ? (
            <img src={row.original.image} alt="" className="h-10 w-10 shrink-0 object-contain" />
          ) : null}
          <div className="min-w-0">
            <div className="truncate font-medium" title={row.original.name}>
              {row.original.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {row.original.printing ?? "—"}{" "}
              {row.original.tcgPlayerUrl ? (
                <a
                  className="underline"
                  href={row.original.tcgPlayerUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  TCGPlayer
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ),
    }),
    queueColumns.accessor("printing", {
      header: "Printing",
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor("urgency", {
      header: "Urgency",
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor("targetOnHand", {
      header: "Qty target",
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor("toOrder", {
      header: "To order",
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor("urgentQuantity", {
      header: "Urgent",
      meta: { align: "end", filter: "number" },
    }),
    queueColumns.accessor((row) => (row.decision === "NOT_NEEDED" ? "Not needed" : row.decision), {
      id: "decision",
      header: "Decision",
      meta: { filter: "text" },
    }),
    queueColumns.accessor("marketCents", {
      header: "Market",
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("targetCents", {
      header: "Target",
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("maxCents", {
      header: "Max",
      cell: ({ getValue }) => centsToDollars(getValue()),
      meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
    }),
    queueColumns.accessor("category", {
      header: "Category",
      cell: ({ getValue }) => getValue() ?? "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor((row) => gameLabel(row.game) ?? "", {
      id: "game",
      header: "Game",
      cell: ({ getValue }) => getValue() || "—",
      meta: { filter: "text" },
    }),
    queueColumns.accessor("tcgPlayerUrl", {
      header: "TCGPlayer URL",
      meta: { hidden: true, csv: (value) => (typeof value === "string" ? value : "") },
    }),
    queueColumns.display({
      id: "add",
      size: 72,
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => addToCart.current(row.original)}>
          Add
        </Button>
      ),
    }),
  ]);
}

/** Retool buying SOP: queue, cart, invoice split, packing list, dispositions. */
export function ProcurementPage() {
  const [tab, setTab] = useState<TabId>("queue");
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Procurement</h1>
        <p className="text-sm text-muted-foreground">
          Standing needs, TCGPlayer buying, and per-card dispositions. Target is the market price.
          Max is the gem price, or 20% above it on a card under $5. ORDER may pay up to max
          including shipping, BUY_LOW up to target. Those are soft warnings.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <Button
            key={item.id}
            size="sm"
            variant={tab === item.id ? "default" : "outline"}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {tab === "queue" ? <QueuePanel /> : null}
      {tab === "orders" ? <OrdersPanel /> : null}
      {tab === "receive" ? <ReceivePanel /> : null}
      {tab === "uncosted" ? <UncostedPanel /> : null}
      {tab === "create" ? <CreatePanel /> : null}
      {tab === "irregularities" ? <IrregularitiesPanel /> : null}
    </div>
  );
}

function QueuePanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const queue = useQuery(trpc.platform.fulfillment.procurement.queue.queryOptions());
  const [cart, setCart] = useState<CartLine[]>([]);
  const [vendor, setVendor] = useState("TCGPlayer");
  const [invoiceDollars, setInvoiceDollars] = useState("");
  const [unmatched, setUnmatched] = useState<Array<{ catalogItemId: string; quantity: number }>>(
    [],
  );

  const submit = useMutation(
    trpc.platform.fulfillment.procurement.orders.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Buying order created.");
        setCart([]);
        setInvoiceDollars("");
        await queryClient.invalidateQueries();
      },
    }),
  );
  const packing = useMutation(
    trpc.platform.fulfillment.procurement.orders.uploadPackingList.mutationOptions({
      onSuccess: async (result) => {
        setUnmatched(result.unmatched);
        if (result.order) toast.success("Packing list submitted.");
        else toast.success("No matched rows to order.");
        await queryClient.invalidateQueries();
      },
    }),
  );

  const rows = queue.data?.rows ?? [];
  const totals = queue.data?.totals;

  function addToCart(row: BuyingQueueRow) {
    if (cart.length >= 99 && !cart.some((l) => l.catalogItemId === row.catalogItemId)) {
      toast.success("Cart is capped at 99 SKUs.");
      return;
    }
    setCart((prev) => {
      const existing = prev.find((l) => l.catalogItemId === row.catalogItemId);
      if (existing) {
        return prev.map((l) =>
          l.catalogItemId === row.catalogItemId ? { ...l, quantity: l.quantity + 1 } : l,
        );
      }
      return [
        ...prev,
        {
          catalogItemId: row.catalogItemId,
          name: row.name,
          quantity: row.urgentQuantity || 1,
          marketCents: row.marketCents,
          decision: row.decision,
        },
      ];
    });
  }

  const invoiceCents = Math.round(Number(invoiceDollars) * 100);
  const addToCartRef = useRef(addToCart);
  addToCartRef.current = addToCart;
  const columns = useMemo(() => buildQueueColumns(addToCartRef), []);

  if (queue.isError) return <ErrorAlert message={queue.error.message} />;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <Card>
        <CardHeader>
          <CardTitle>Queue</CardTitle>
        </CardHeader>
        <CardContent>
          {queue.isPending ? (
            <Skeleton className="h-40" />
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(row) => row.catalogItemId}
              pinned={QUEUE_PINNED}
              search="Search items"
              csvName="buying-queue"
              emptyMessage="Nothing to buy."
            />
          )}
          {totals ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Queue at 80% of market: {centsToDollars(totals.queueValueAtEightyPercentCents)} · to
              order {totals.totalQuantity}
              {urgentByGameSummary(totals.urgentQuantityByGame)}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Cart ({cart.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {cart.map((line) => (
              <div key={line.catalogItemId} className="text-sm">
                <label htmlFor={`cart-qty-${line.catalogItemId}`}>{line.name}</label>
                <Input
                  id={`cart-qty-${line.catalogItemId}`}
                  className="mt-1"
                  type="number"
                  min={1}
                  value={line.quantity}
                  onChange={(event) => {
                    const quantity = Number(event.target.value);
                    setCart((prev) =>
                      prev.map((l) =>
                        l.catalogItemId === line.catalogItemId ? { ...l, quantity } : l,
                      ),
                    );
                  }}
                />
              </div>
            ))}
            <Input
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
              aria-label="Vendor"
            />
            <Input
              placeholder="Invoice total (dollars)"
              value={invoiceDollars}
              onChange={(event) => setInvoiceDollars(event.target.value)}
            />
            <Button
              disabled={cart.length === 0 || !Number.isFinite(invoiceCents) || invoiceCents <= 0}
              onClick={() =>
                submit.mutate({
                  vendor,
                  invoiceTotalCents: invoiceCents,
                  lines: cart.map((l) => ({
                    catalogItemId: l.catalogItemId,
                    quantity: l.quantity,
                    marketCents: l.marketCents,
                  })),
                })
              }
            >
              Submit order
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Packing list</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void file
                  .text()
                  .then((csvText) => packing.mutate({ csvText, filename: file.name }));
              }}
            />
            {unmatched.length > 0 ? (
              <ul className="mt-3 text-sm text-muted-foreground">
                {unmatched.map((row) => (
                  <li key={row.catalogItemId}>
                    Unmatched {row.catalogItemId} × {row.quantity}
                  </li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function delayLabel(orderDate: Date | string): string {
  const ms = Date.now() - new Date(orderDate).getTime();
  const days = Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
  return `${days}d`;
}

function OrdersPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orders = useQuery(trpc.platform.fulfillment.procurement.orders.list.queryOptions());
  const cancel = useMutation(
    trpc.platform.fulfillment.procurement.orders.cancel.mutationOptions({
      onSuccess: async () => {
        toast.success("Buying order cancelled.");
        await queryClient.invalidateQueries();
      },
    }),
  );
  if (orders.isError) return <ErrorAlert message={orders.error.message} />;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent buying orders</CardTitle>
      </CardHeader>
      <CardContent>
        {orders.isPending ? (
          <Skeleton className="h-32" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delay</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Units</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(orders.data ?? []).map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.vendor}</TableCell>
                  <TableCell>{order.source}</TableCell>
                  <TableCell>
                    {order.source === "BUYLIST" && order.buylistStatus
                      ? `${order.status} / ${order.buylistStatus}`
                      : order.status}
                  </TableCell>
                  <TableCell>{delayLabel(order.orderDate)}</TableCell>
                  <TableCell>{centsToDollars(order.invoiceTotalCents)}</TableCell>
                  <TableCell>{order.unitCount}</TableCell>
                  <TableCell>
                    {order.source === "INTERNAL" && order.status === "ORDERED" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancel.mutate({ id: order.id })}
                      >
                        Undo
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ReceivePanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [catalogItemId, setCatalogItemId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const inTransit = useQuery(
    trpc.platform.fulfillment.procurement.units.listByStatus.queryOptions({ status: "IN_TRANSIT" }),
  );
  const receive = useMutation(
    trpc.platform.fulfillment.procurement.units.receive.mutationOptions({
      onSuccess: async () => {
        toast.success("Received.");
        await queryClient.invalidateQueries();
      },
    }),
  );
  const dispose = useMutation(
    trpc.platform.fulfillment.procurement.units.dispose.mutationOptions({
      onSuccess: async () => {
        toast.success("Updated.");
        await queryClient.invalidateQueries();
      },
    }),
  );
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Receive by catalog item</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Input
            placeholder="Catalog item id"
            value={catalogItemId}
            onChange={(event) => setCatalogItemId(event.target.value)}
          />
          <Input
            type="number"
            min={1}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="w-24"
          />
          <Button onClick={() => receive.mutate({ catalogItemId, quantity: Number(quantity) })}>
            Receive
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>In transit</CardTitle>
        </CardHeader>
        <CardContent>
          {inTransit.isPending ? (
            <Skeleton className="h-32" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(inTransit.data ?? []).map((unit) => (
                  <TableRow key={unit.id}>
                    <TableCell className="font-mono text-xs">{unit.id}</TableCell>
                    <TableCell className="font-mono text-xs">{unit.catalogItemId}</TableCell>
                    <TableCell>{centsToDollars(unit.unitCostCents)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          dispose.mutate({
                            id: unit.id,
                            to: "DAMAGED",
                            damageOrigin: "IN_TRANSIT",
                          })
                        }
                      >
                        Damaged
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UncostedPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const rows = useQuery(trpc.platform.fulfillment.procurement.units.uncosted.queryOptions());
  const setCost = useMutation(
    trpc.platform.fulfillment.procurement.units.setCost.mutationOptions({
      onSuccess: async () => {
        toast.success("Cost saved.");
        await queryClient.invalidateQueries();
      },
    }),
  );
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  if (rows.isError) return <ErrorAlert message={rows.error.message} />;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Items without a purchase price</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.isPending ? (
          <Skeleton className="h-32" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unit</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rows.data ?? []).map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell className="font-mono text-xs">{unit.id}</TableCell>
                  <TableCell>{unit.costSource}</TableCell>
                  <TableCell>
                    <Input
                      value={drafts[unit.id] ?? String(unit.unitCostCents / 100)}
                      onChange={(event) =>
                        setDrafts((prev) => ({ ...prev, [unit.id]: event.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      onClick={() =>
                        setCost.mutate({
                          id: unit.id,
                          unitCostCents: Math.round(
                            Number(drafts[unit.id] ?? unit.unitCostCents / 100) * 100,
                          ),
                        })
                      }
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function CreatePanel() {
  const trpc = useTRPC();
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("0");
  const [alreadyProcured, setAlreadyProcured] = useState(true);
  const duplicates = useQuery({
    ...trpc.platform.fulfillment.items.likelyDuplicates.queryOptions({ name }),
    enabled: name.trim().length > 2,
  });
  const create = useMutation(
    trpc.platform.fulfillment.items.create.mutationOptions({
      onSuccess: () => toast.success("Catalog item created."),
    }),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Catalog create</CardTitle>
      </CardHeader>
      <CardContent className="flex max-w-md flex-col gap-3">
        <Input placeholder="Name" value={name} onChange={(event) => setName(event.target.value)} />
        {(duplicates.data ?? []).length > 0 ? (
          <p className="text-sm text-amber-600">
            Similar items: {(duplicates.data ?? []).map((d) => d.name).join(", ")}
          </p>
        ) : null}
        <Input
          type="number"
          min={0}
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={alreadyProcured}
            onChange={(event) => setAlreadyProcured(event.target.checked)}
          />
          Already on hand (spawn IN_INVENTORY units)
        </label>
        <Button
          onClick={() =>
            create.mutate({
              name,
              pricingSource: "MANUAL",
              availabilityStrategy: "DYNAMIC",
              quantity: Number(quantity),
              alreadyProcured,
            })
          }
        >
          Create
        </Button>
      </CardContent>
    </Card>
  );
}

function IrregularitiesPanel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const snap = useQuery(trpc.platform.fulfillment.procurement.irregularities.queryOptions());
  const damaged = useQuery(
    trpc.platform.fulfillment.procurement.units.listByStatus.queryOptions({ status: "DAMAGED" }),
  );
  const recover = useMutation(
    trpc.platform.fulfillment.procurement.units.recoverDamaged.mutationOptions({
      onSuccess: async () => {
        toast.success("Recovered to inventory.");
        await queryClient.invalidateQueries();
      },
    }),
  );
  if (snap.isError) return <ErrorAlert message={snap.error.message} />;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Latest snapshot</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {snap.data ? (
            <ul className="space-y-1">
              <li>Day {snap.data.day}</li>
              <li>Damaged {centsToDollars(snap.data.damagedCostCents)}</li>
              <li>Lost in transit {centsToDollars(snap.data.lostInTransitCostCents)}</li>
              <li>Customer support {centsToDollars(snap.data.customerSupportCostCents)}</li>
              <li>Count adjustment {centsToDollars(snap.data.countAdjustmentCostCents)}</li>
            </ul>
          ) : (
            <p className="text-muted-foreground">No snapshot yet.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Damaged units</CardTitle>
        </CardHeader>
        <CardContent>
          {(damaged.data ?? []).map((unit) => (
            <div key={unit.id} className="flex items-center justify-between py-1 text-sm">
              <span className="font-mono text-xs">{unit.id}</span>
              <Button size="sm" variant="outline" onClick={() => recover.mutate({ id: unit.id })}>
                Recover
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
